import i18n from 'src/i18n'
import Model from 'src/models/model'
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import { AVAILABILITY_REASONS, useAvailabilityResult } from 'src/modules/checkout/composables/sales-availability/AvailabilityResult'
import { isOutOfStock } from 'src/services/inventory/sales-restrictions'
import { toValue } from 'vue'

// Options is a hash containing fields that can be used to validate modifier availability.
const DEFAULT_OPTIONS = Object.freeze({
  salesChannel: null,
  orderItem: null
})

export function useIsModifierAvailable () {
  const { result } = useAvailabilityResult()
  const currentSite = Model.configuration?.currentSite

  function isModifierAvailable (modifier, { salesChannel, orderItem } = DEFAULT_OPTIONS) {
    const modifierValue = toValue(modifier)

    if (_.isNil(modifierValue)) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_NIL,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_NIL}`)
      })
    }

    const salesChannelValue = toValue(salesChannel)
    const orderItemValue = toValue(orderItem)

    // The Modifier must be active (not archived) to be presented
    if (modifierValue.archivable && !modifierValue.isActive) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_ARCHIVED,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_ARCHIVED}`)
      })
    }

    // The Modifier must be available on the current Sales Channel
    if (!modifierValue.isSalesChannelEnabled(salesChannelValue)) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_DISABLED_ON_SALES_CHANNEL,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_DISABLED_ON_SALES_CHANNEL}`, { salesChannel: _.startCase(salesChannelValue) })
      })
    }

    // If the Sales Channel is private, the Modifier is available, regardless of
    // Inventory or Sales Toggle (the Modifier may be shown without being selectable)
    if (_.includes(SALES_CHANNEL_TYPES.PRIVATE, salesChannelValue)) {
      return result({
        isAvailable: true
      })
    }

    if (modifierValue.salesDisabled) {
      return result({
        availableReason: AVAILABILITY_REASONS.IS_SALES_DISABLED,
        availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_SALES_DISABLED}`)
      })
    }

    if (!_.isNil(orderItemValue?.itemOrderItem?.item) && currentSite?.activeInventoryStrategy?.excludeOutOfStockItemsForKioskAndConsumer) {
      if (isOutOfStock(modifierValue, { item: orderItemValue?.itemOrderItem?.item, itemOrderItem: orderItemValue?.itemOrderItem, orderItem: orderItemValue })) {
        return result({
          availableReason: AVAILABILITY_REASONS.IS_OUT_OF_STOCK,
          availableMessage: i18n.global.t(`availability.${AVAILABILITY_REASONS.IS_OUT_OF_STOCK}`)
        })
      }
    }

    return result({
      isAvailable: true
    })
  }

  return {
    isModifierAvailable
  }
}
